import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Moment } from 'moment';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { Constants } from '../../core/constants';
import { ProcessingOverlayParam } from '../../models/processing-overlay-param.model';
import { BroadcastService } from '../../services/broadcast.service';
import { LoggingService } from '../../services/logging.service';
import { MessageBoxService } from '../../services/message-box.service';
import { OverlayManagerService } from '../../services/overlay-manager.service';
import { UtilityService } from '../../services/utility.service';
import { MessageBoxButton, MessageBoxType } from '../../shared/message-box/message-box.component';
import { Toro } from '../common/enumerations';
import { CommCardWithSmartConnectId } from '../models/comm-card/comm-card-with-smart-connect-id.model';
import { EnableNewFeaturesInfo } from '../models/enable-new-features-info.model';
import { AccountFirmwareUpdateInfo } from '../models/firmware-update/account-firmware-update-info.model';
import { IFirmware } from '../models/IFirmware';
import { ControllerRecordChangeNotificationModel } from '../models/notifications/controller-record-change-notification.model';
import { ControllerStatusChangedNotificationModel } from '../models/notifications/controller-status-changed-notification.model';
import { EvoAddReplaceProcessUpdateNotification } from '../models/notifications/evo-add-replace-process-update-notification.model';
import { FirmwareUpgradeProgressNotification } from '../models/notifications/firmware-upgrade-progress-notification.model';
import { MailCenterNotification } from '../models/notifications/mail-center-notification.model';
import { ConnectedDeviceWithFirmwareInfo } from '../models/user-registration/connected-device-with-firmware-info.model';
import { FirmwareApiService } from '../services/firmware-api.service';
import { ControllerManagerService } from './controller-manager.service';
import { LocalCacheManager } from './local-cache.manager';
import { LocationManagerService } from './location-manager.service';
import { NotificationManagerService } from './notification-manager.service';
import { DeviceFwUpdateInfoListItem } from '../models/firmware-update/device-fw-update-info-list-item.model';
import { startsWith } from 'lodash';
import { ConfigsAreDifferentNotification } from '../models/notifications/configs-are-different-notification';
import { LxcAddReportNotification } from '../models/notifications/lxc-add-report-notification.model';

@Injectable()
export class FirmwareManagerService implements OnDestroy {
    // Subjects
    firmwareUpgraded = new Subject<string>();
    firmwareUpgradeCancelled = new Subject();
    firmwareUpgradeInProgress = new Subject();
    noFirmwareUpgradeReceived = new Subject();
    evoRequiresUsbFlashDriveUpdate = new Subject();
    reloadData = new Subject();
    enableNewFeatureInfoChanged = new Subject<EnableNewFeaturesInfo>();
    fwUpdatePercentCompleteChange = new Subject<FirmwareUpgradeProgressNotification>();
    deviceDiscoveryReportReceived = new Subject();
    installingFirmwareOnDevice = new Subject();
    remappingRecords = new Subject();

    private _firmwareUpgradeProgressNotification: FirmwareUpgradeProgressNotification;
    private _firmwareInfoDict: { [accountId: number]: IFirmware[]; } = {};
    private _enableNewFeaturesInfoDict: { [controllerId: number]: EnableNewFeaturesInfo } = {};
    public isUpgradingFirmware = false;
    private hasProgressUpdateStarted = false;
    public infoText: string = null;
    private _isPendingConfigurationRetrieval = false;
    private timeToWaitInSeconds: number = null;
    private firmwareCountdownInterval: any;
    private firmwareProgressTimerRef: any;
    private firmwareDeviceListForUpgrade: Array<DeviceFwUpdateInfoListItem>;
    private gatewayFirmwareProgressTimerRef: any;
    private magnetoFirmwareProgressTimerRef: any;
    private lxcFirmwareProgressTimerRef: any;
    private locationId: number;
    private lxcAddReportNotificationSubscription: Subscription;
    private isReplaceSMRTR:boolean;
    // =========================================================================================================================================================
    // C'tor and Destroy
    // =========================================================================================================================================================

    constructor(private router: Router,
        private controllerManager: ControllerManagerService,
        private firmwareApiService: FirmwareApiService,
        private notificationManager: NotificationManagerService,
        private messageBoxService: MessageBoxService,
        private locationManager: LocationManagerService,
        private localCacheManager: LocalCacheManager,
        private overlayManager: OverlayManagerService,
        private broadcastService: BroadcastService) {
        // Clear any temporary flags on init/refresh.
        this.localCacheManager.FwUpdateCancelled = false;

        this.broadcastService.userSignedOut
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.clearCache();
            });

        this.notificationManager.firmwareUpgradeProgressNotificationReceived
            .pipe(untilDestroyed(this))
            .subscribe((response: FirmwareUpgradeProgressNotification) => {
                this.overlayManager.hideProcessingOverlay.next(); // hide firmware upgrade started spinner
                if (this.localCacheManager.FwUpdateCancelled) return;

                this.firmwareUpgradeInProgress.next();
                this.hasProgressUpdateStarted = true;

                if (response.DeviceType === Toro.DeviceTypeId.KD2) response.DeviceDescription = 'KD2';

                // if (response.DeviceType === Toro.DeviceTypeId.MAGNETO) response.DeviceDescription = '';

                this._firmwareUpgradeProgressNotification = response;
                this.fwUpdatePercentCompleteChange.next(this._firmwareUpgradeProgressNotification);
                this.handleFirmwareUpgradeProgressNotification();

                //LoggingService.log(response);

                if (this._firmwareUpgradeProgressNotification && this._firmwareUpgradeProgressNotification.PercentComplete < 98) {
                    if (this.overlayManager.isProcessing) this.overlayManager.hideProcessingOverlay.next();
                    if (!this.overlayManager.isBlurred) this.overlayManager.toggleBlurBackground.next(true);
                }
            });

            this.notificationManager.configsAreDifferentNotificationReceived
            .pipe(untilDestroyed(this))
            .subscribe((response: ConfigsAreDifferentNotification) => {
                console.log('response configsAreDifferentNotificationReceived ',response);
                if(this.firmwareUpgradeProgressNotification && (response.DeviceId == this.firmwareUpgradeProgressNotification.DeviceId)){
                    if(response.Differences && response.Differences.length > 0){

                        if (this.overlayManager.isProcessing) this.overlayManager.hideProcessingOverlay.next();
                        if (!this.overlayManager.isBlurred) this.overlayManager.toggleBlurBackground.next(true);
                        LoggingService.log('>> ROGUE 6 <<');
                        this.upgradeComplete();
                    }
                }
            });

        this.notificationManager.mailCenterNotificationReceived
            .pipe(untilDestroyed(this))
            .subscribe((notification: MailCenterNotification) => {
                if (notification.SubjectTitle.includes('Firmware Upgrade Cancelled')) {
                    this._firmwareUpgradeProgressNotification = null;
                    this.localCacheManager.FwUpdateCancelled = true;
                    this.clearCacheInfo();
                    this.hasProgressUpdateStarted = false;

                    setTimeout(() => { this.localCacheManager.removeFwUpdateCancelled(); }, 30000);
                }
                else if (notification.DisplayableMessage.includes('Status: Failed')) {
                    const msg = Constants.Strings.SMRTScapeUnableToUpdateDevice;
                    this._firmwareUpgradeProgressNotification = null;
                    this.localCacheManager.FwUpdateCancelled = true;
                    this.clearCacheInfo();
                    this.hasProgressUpdateStarted = false;
                    // notification sends reason for failed inspira upgrade
                    this.messageBoxService.displayMessageBox(msg, MessageBoxType.Error, this.resetFwUpdateCancelledFlag.bind(this), this.resetFwUpdateCancelledFlag.bind(this), notification.SubjectTitle);
                    // setTimeout(() => { this.localCacheManager.removeFwUpdateCancelled(); }, 30000);
                }
            });

        this.notificationManager.evoAddReplaceProcessUpdateNotificationReceived
            .pipe(untilDestroyed(this))
            .subscribe((notification: EvoAddReplaceProcessUpdateNotification) => {
                this.processEvoAddReplaceProcessUpdateNotification(notification);
            });

        this.notificationManager.irrigationControllerStatusChanged
            .pipe(untilDestroyed(this))
            .subscribe((response: ControllerStatusChangedNotificationModel) => {
                if (response.IncomingConfiguration.TemporaryForDevOnlyR0 == null) return;

                this.handlePendingConfigRetrieval();
            });

        this.notificationManager.irrigationControllerRecordChange
            .pipe(untilDestroyed(this))
            .subscribe((response: ControllerRecordChangeNotificationModel) => {
                this.handlePendingConfigRetrieval();
            });

        this.notificationManager.deviceDiscoveryReportReceived
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.deviceDiscoveryReportReceived.next();
            });
    }

    resetFwUpdateCancelledFlag() {
        this.localCacheManager.FwUpdateCancelled = false
    }

    ngOnDestroy() {
        if (this.firmwareCountdownInterval) {
            clearInterval(this.firmwareCountdownInterval);
        }
        this.clearGatewayFirmwareProgressTimer();
        this.clearMagnetoFirmwareProgressTimer();
        this.clearLXCFirmwareProgressTimer();
    }

    clearAllTimers(){
        if (this.firmwareCountdownInterval) {
            clearInterval(this.firmwareCountdownInterval);
        }
        this.clearGatewayFirmwareProgressTimer();
        this.clearMagnetoFirmwareProgressTimer();
        this.clearLXCFirmwareProgressTimer();
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================

    get firmwareUpgradeProgressNotification(): FirmwareUpgradeProgressNotification {
        return this._firmwareUpgradeProgressNotification || null;
    }

    getEnableNewFeaturesInfo(controllerId: number): EnableNewFeaturesInfo {
        return this._enableNewFeaturesInfoDict[controllerId] || null;
    }

    getAccountFirmwareInfo(accountId: number, cached: boolean = true): Observable<IFirmware[]> {
        if (cached && this._firmwareInfoDict[accountId]) {
            return of(this._firmwareInfoDict[accountId]);
        }

        return this.firmwareApiService.getAccountFirmwareWithInfo(accountId)
            .pipe(
                map((devicesWithFirmwareInfo: ConnectedDeviceWithFirmwareInfo[]) => {
                    this._firmwareInfoDict[accountId] = [];
                    devicesWithFirmwareInfo.forEach(device => {
                        this._firmwareInfoDict[accountId].push(new ConnectedDeviceWithFirmwareInfo(device));
                        if (device.CommCardWithSmartConnectId) {
                            this._firmwareInfoDict[accountId].push(new CommCardWithSmartConnectId(device.CommCardWithSmartConnectId));
                        }
                    });

                    return this._firmwareInfoDict[accountId];
                })
            );
    }

    getAccountFirmwareUpdateInfo(accountId: number): Observable<AccountFirmwareUpdateInfo> {
        return this.firmwareApiService.getAccountFirmwareUpdateInfo(accountId);
    }

    setFirmwareDeviceListForUpgrade(deviceList: Array<DeviceFwUpdateInfoListItem>) {
        this.firmwareDeviceListForUpgrade = deviceList;
    }
    getFirmwareDeviceListForUpgrade(): Array<DeviceFwUpdateInfoListItem> {
        return this.firmwareDeviceListForUpgrade;
    }

    upgradeFirmware(accountId: number, selectedDevicesToUpgradeFirmware: number[]): Observable<any> {
        if (selectedDevicesToUpgradeFirmware && selectedDevicesToUpgradeFirmware.length == 1) {
            return this.upgradeDeviceFirmware(accountId, selectedDevicesToUpgradeFirmware);
        } else {
            return this.upgradeDevicesFirmware(accountId, selectedDevicesToUpgradeFirmware);
        }
    }

    upgradeDeviceFirmware(accountId: number, selectedDevicesToUpgradeFirmware: number[]): Observable<any> {
        return this.firmwareApiService.upgradeFirmware(accountId, selectedDevicesToUpgradeFirmware)
            .pipe(
                tap(response => {
                    if (!this.isUpgradingFirmware) {
                        this.isUpgradingFirmware = true;
                    }
                })
            );
    }

    upgradeDevicesFirmware(accountId: number, selectedDevicesToUpgradeFirmware: number[]): Observable<any> {
        return this.firmwareApiService.upgradeDevicesFirmware(accountId, selectedDevicesToUpgradeFirmware)
            .pipe(
                tap(response => {
                    if (!this.isUpgradingFirmware) {
                        this.isUpgradingFirmware = true;
                    }
                })
            );
    }

    manuallyResetFirmwareUpgradeState() {
        this._firmwareUpgradeProgressNotification = null;
        this.infoText = null;
    }

    cancelFirmwareUpgrade(deviceId: number, deviceTypeId: number): Observable<any> {
        return this.firmwareApiService.cancelFirmwareUpgrade(deviceId, deviceTypeId);
    }

    isFirmwareUpdateActive(deviceUId: string) {
        const shortUId = (deviceUId.length === 6) ? deviceUId : UtilityService.getShortUIdFromFullUId(deviceUId);
        const now = moment();

        return this.isDeviceFwUpdateActive(shortUId, now) || this.isGatewayFwUpdateActive(shortUId, now)
        || this.isEvoAddReplaceActive(shortUId, now) || this.isLXCAddReplaceActive(shortUId,now);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private handlePendingConfigRetrieval() {
        if (this._isPendingConfigurationRetrieval) {
            this._isPendingConfigurationRetrieval = false;
            LoggingService.log('Config received!');
            LoggingService.log('>> ROGUE 1 <<');
            this.upgradeComplete();
        }
    }

    private clearCache() {
        this._firmwareInfoDict = {};
    }

    private clearCacheInfo() {
        const lsKeys = Object.keys(localStorage);
        lsKeys.forEach(function (key) {
            if (key.startsWith('evoAddReplaceActiveId:') ||
                key.startsWith('gatewayFwCompletionPendingGwId') ||
                key.startsWith('activeFwUpdateDeviceUId') ||
                key.startsWith('LXCAddReplaceActiveId')) {
                localStorage.removeItem(key);
            }
        });
    }

    private firmwareUpgradedCallback() {
        if (this.hasProgressUpdateStarted === false) {
            setTimeout(() => {
                if (this.infoText != null || this.hasProgressUpdateStarted === true) {
                    LoggingService.log('>> ROGUE 2 <<');
                    this.upgradeComplete();
                }
            }, 60 * 1000);
            return;
        }
        LoggingService.log('>> ROGUE 3 <<');
        this.upgradeComplete();
    }

    private upgradeComplete() {
        this.infoText = null;
        this._firmwareUpgradeProgressNotification = null;
        this.isUpgradingFirmware = false;
        this._firmwareInfoDict = {};
        this.hasProgressUpdateStarted = false;

        // Force a refresh of any cached controller lists.
        this.controllerManager.clearCache();
        let message = this.hasProgressUpdateStarted ? Constants.Strings.FirmwareUpgradeCompleteMessage :
            Constants.Strings.OperationCompletedSuccessfully;
        this.firmwareUpgraded.next(message);
        if (this.firmwareCountdownInterval) clearInterval(this.firmwareCountdownInterval);

        this.clearAllTimers();
    }

    private processEvoAddReplaceProcessUpdateNotification(notification: EvoAddReplaceProcessUpdateNotification) {
        let gwShortUId;
        const controllerShortUId = UtilityService.getShortUIdFromFullUId(notification.Controller.UId);
        let evoRequiresUsbFlashDriveUpdate = false;

        if (notification.Gateway) {
            gwShortUId = UtilityService.getShortUIdFromFullUId(notification.Gateway.UId);
        }

        this.timeToWaitInSeconds = null;

        switch (notification.NotificationType) {
            case 'DeviceAddedInSystem':
                if(notification.Operation.includes('ReplaceSMRTR')){
                    this.isReplaceSMRTR = true;
                }else if(notification.Operation.includes('AddSMRTR')){

                    if(notification && notification.Controller && notification.Controller.LocationId){
                        this.locationId = notification.Controller.LocationId;

                        this.lxcAddReportNotificationSubscription = this.notificationManager.lxcAddReportNotificationReceived
                        .pipe(untilDestroyed(this))
                        .subscribe((notification: LxcAddReportNotification) => {
                                console.log('LN: LXCBlocksRecievedSuccess  ',notification.BlockId);
                                if(notification.BlockId == Toro.LxcBlockId.HEADER_BLOCK){
                                    this.overlayManager.hideProcessingOverlay.next();
                                    if(this.lxcAddReportNotificationSubscription){
                                        this.lxcAddReportNotificationSubscription.unsubscribe();
                                    }
                                    this.messageBoxService.displayMessageBox(Constants.Strings.LXCBlocksRecievedSuccess, MessageBoxType.Success,
                                    () => { this.goToDashboard(this.locationId, false) },
                                    () => { this.goToDashboard(this.locationId, false) });
                                }
                        });
                    }
                }
                break;

            case 'OperationSuccess':
                if (notification.IsLastStep) {
                    // If we are adding an EVO or KD2, we MUST wait for the Config to be retrieved.
                    if (notification.Controller.isEvo
                        || notification.Controller.isKD2
                        || (notification.Controller.isRD && !notification.Operation.includes('ReplaceCL'))
                        && !notification.Controller.isLXC) {
                        this.installingFirmwareOnDevice.next();
                        this._isPendingConfigurationRetrieval = true;
                        this.timeToWaitInSeconds = (this.infoText !== `${Constants.Strings.InstallingFwOnController}...`) ? 120 : 420;
                        this.infoText = `${Constants.Strings.SynchronizingConfigWithController}...`;
                    } else {
                        LoggingService.log('>> ROGUE 5 <<');
                        this.upgradeComplete();
                    }
                }
                break;

            case 'RemappingRecords':
                this.remappingRecords.next();
                this.infoText = Constants.Strings.UpdatingControllerCommunicationSettings;
                this.timeToWaitInSeconds = 200;
                break;

            case 'GwFirmwareUpgradeComplete':
            case 'EvoGwFirmwareUpgradeComplete':
                // Clear any pending gateway fw upgrade completion flags.
                localStorage.removeItem('gatewayFwCompletionPendingGwId:' + gwShortUId);

                // If the GW FW update was not the last step, we can assume we need a FW upgrade for the SC or EVO.
                // Enabling/disable any buttons associated with FW upgrade. It will only affect the active page.
                if (!notification.IsLastStep) {
                    localStorage.setItem('activeFwUpdateDeviceUId:' + controllerShortUId, new Date().toString());
                    this._enableNewFeaturesInfoDict[notification.Controller.Id] =
                        new EnableNewFeaturesInfo(notification.Controller.Id, true, Constants.Strings.UpdateInProgress);
                    this.infoText = Constants.Strings.UpdateInProgress;
                }
                break;

            case 'GwFirmwareUpgradeStart':
            case 'EvoGwFirmwareUpgradeStart':
                this.infoText = null;
                localStorage.setItem('gatewayFwCompletionPendingGwId:' + gwShortUId, new Date().toString());
                localStorage.setItem('activeFwUpdateDeviceUId:' + gwShortUId, new Date().toString());

                // upgrading gateway is always the first step
                const gatewayUpdateRequiredMessage = Constants.Strings.GatewayUpdateRequiredMessage;
                this.messageBoxService.displayMessageBox(gatewayUpdateRequiredMessage, MessageBoxType.Info, () => {
                    this.messageBoxService.closeMessageBox();
                    if (this.hasProgressUpdateStarted) { return; }

                    this.overlayManager.showProcessingOverlay.next(Constants.Strings.PreparingFirmwareUpgrade);
                    this.checkIfStillPreparingForUpgrade();
                }, null, Constants.Strings.FirmwareUpgradeRequired);
                break;

            case 'CommCardFirmwareUpgradeStart':
            case 'ControllerFirmwareUpgradeStart':
            case 'EvoScFirmwareUpgradeStart':
            case 'EvoFirmwareUpgradeStart':
                this.infoText = null;
                this._firmwareUpgradeProgressNotification = null;

                if (!this.hasProgressUpdateStarted) {
                    this.overlayManager.hideProcessingOverlay.next();

                    let fwUpgradeRequiredMessage = `The controller ${notification.Controller.Name} was added, but appears to have firmware that is out of date. \
                            The controller\'s functionality will be limited until the firmware is upgraded. The firmware will be automatically updated (this \
                            can take a couple hours).`;

                    if (notification.Controller.isEvo) {
                        fwUpgradeRequiredMessage += '<br /><br /> Alternatively, you can go ' +
                            '<a style=\'font-weight: normal\' href=\'http://sites.toro.com/evolution/download-user-manual.html\' target=\'_blank\'>here</a> ' +
                            'to manually update the controller.';
                    }

                    this.messageBoxService.displayMessageBox(fwUpgradeRequiredMessage, MessageBoxType.Info, () => {
                        this.messageBoxService.closeMessageBox();
                        if (this.hasProgressUpdateStarted) { return; }

                        this.overlayManager.showProcessingOverlay.next(Constants.Strings.PreparingFirmwareUpgrade);
                        this.checkIfStillPreparingForUpgrade();
                    }, null, Constants.Strings.FirmwareUpgradeRequired);
                }

                localStorage.setItem('activeFwUpdateDeviceUId:' + controllerShortUId, new Date().toString());
                this._enableNewFeaturesInfoDict[notification.Controller.Id] =
                    new EnableNewFeaturesInfo(notification.Controller.Id, true, Constants.Strings.FirmwareUpgrading);
                break;

            case 'CommCardFirmwareUpgradeComplete':
            case 'ControllerFirmwareUpgradeComplete':
            case 'EvoScFirmwareUpgradeComplete':
            case 'EvoFirmwareUpgradeComplete':
                if (!notification.IsLastStep) {
                    LoggingService.log('Firmware upgrade complete!');
                    localStorage.setItem('activeFwUpdateDeviceUId:' + controllerShortUId, new Date().toString());
                    this._enableNewFeaturesInfoDict[notification.Controller.Id] =
                        new EnableNewFeaturesInfo(notification.Controller.Id, true, Constants.Strings.UpdateInProgress);

                    // TODO: MW - Figure this out. Still having issues...
                    if (!notification.Controller.isKD2) {
                        this.infoText = Constants.Strings.UpdateInProgress;
                        this.timeToWaitInSeconds = 150;
                    }
                    else {
                        this.infoText = `${Constants.Strings.InstallingFwOnDevice}...`;
                        this.timeToWaitInSeconds = 450;
                    }
                }
                break;

            case 'RequiresUsbFlashDriveUpdate':
            case 'EvoRequiresUsbFlashDriveUpdate':
                evoRequiresUsbFlashDriveUpdate = true;
                const message = `The controller ${notification.Controller.Name} was added but the Firmware needs to be updated. The controller's functionality
                will be limited until the firmware is upgraded.`;

                const link = 'http://sites.toro.com/evolution/download-user-manual.html';
                const linkMessage = 'A manual firmware upgrade is required. <a href="' + link + '" target="_blank">Click here for help!</a>';
                this.messageBoxService.displayMessageBox(`${message} ${linkMessage}`, MessageBoxType.Info, () => {
                    this.infoText = 'Finishing up...';
                    this.overlayManager.showProcessingOverlay.next(this.infoText);
                    setTimeout(() => {
                        this.infoText = null;
                        this.evoRequiresUsbFlashDriveUpdate.next();
                    }, 3 * 1000);
                });
                break;

            case 'GatewayMarkedAddCmdAsFailed':
                // TODO: Do something meaningful if appropriate.
                this.displayControllerAddedAsNonProgrammableMsg(notification);
                break;

            case 'Exception':
                if (this.firmwareUpgradeProgressNotification != null && this._firmwareUpgradeProgressNotification.PercentComplete >= 98 && !notification.IsLastStep) {
                    this.infoText = Constants.Strings.UpdateInProgress;
                    setTimeout(() => {
                        this._firmwareUpgradeProgressNotification = null;
                        this.clearCacheInfo();
                        setTimeout(() => { this.localCacheManager.removeFwUpdateCancelled(); }, 2 * 60 * 1000);
                        this.firmwareUpgradedCallback();
                    }, Constants.DefaultFwUgExpireTimeInSeconds);
                }
                else if (this._firmwareUpgradeProgressNotification == null || notification.IsLastStep) {
                    this.infoText = null;
                    this.clearCacheInfo();

                    if (notification.ReportedException.Message.includes('No received messages were found in the allotted time')
                        || notification.ReportedException.Message.includes('The requested message was marked as failed')) {
                        // Force a refresh of any cached controller lists.
                        this.controllerManager.clearCache();


                    }
                    this.displayControllerAddedAsNonProgrammableMsg(notification);
                }
                else {
                    this.displayControllerAddedAsNonProgrammableMsg(notification);
                }
                break;
        }

        if (this.infoText && !notification.IsLastStep) {
            const durationInSeconds = this.timeToWaitInSeconds || Constants.DefaultHttpCallTimeoutInSeconds;
            const param = new ProcessingOverlayParam(this.infoText, durationInSeconds, this.unableToProcessRequestCallback.bind(this));
            this.overlayManager.showProcessingOverlay.next(param);
        }

        if (notification.IsLastStep === true) {
            //this.overlayManager.hideProcessingOverlay.next();
            this.clearFwUpdateProgressTimestamps(controllerShortUId);
        }
        else {
            if (!notification.Controller.isLXC) {
                localStorage.setItem('evoAddReplaceActiveId:' + controllerShortUId, new Date().toString());
            } else {
                localStorage.setItem('LXCAddReplaceActiveId:' + controllerShortUId, new Date().toString());
            }
        }

        this.enableNewFeatureInfoChanged.next(this._enableNewFeaturesInfoDict[notification.Controller.Id]);
    }

    private displayControllerAddedAsNonProgrammableMsg(notification: EvoAddReplaceProcessUpdateNotification) {
        this.overlayManager.hideProcessingOverlay.next();
        this.clearFwUpdateProgressTimestamps(UtilityService.getShortUIdFromFullUId(notification.Controller.UId));

        this.messageBoxService.displayMessageBox('The controller was added, but it has been configured as a non-programmable controller. ' +
            '<br /><br />SMRTScape must be able to connect to the controller and retrieve its configuration before it can be utilized as a ' +
            'programmable controller.<br/><br/>Wait for the controller to automatically update itself (within the next 30 minutes).',
            MessageBoxType.Error, () => {
                this.router.navigate([`/dashboard/${notification.Controller.LocationId}/irrigation/main`], { queryParams: { useCache: false } });
            }, null, Constants.Strings.ControllerAddedInNonProgrammableMode);
    }

    private unableToProcessRequestCallback() {
        this.messageBoxService.displayMessageBox(`${Constants.Strings.UnableToProcessRequest}`, MessageBoxType.Error);
    }

    private handleFirmwareUpgradeProgressNotification() {
        let deviceTypeName = '';

        if ((this._firmwareUpgradeProgressNotification.StatusCode == Toro.StatusCodes.Unknown)
            || (this._firmwareUpgradeProgressNotification.StatusCode == Toro.StatusCodes.FailedDueToSPIRetry)
            || (this._firmwareUpgradeProgressNotification.StatusCode == Toro.StatusCodes.FailedDueToDownloadCancel)
            || this._firmwareUpgradeProgressNotification.StatusCode == Toro.StatusCodes.FailedDueToNoRf) {
                this.infoText = null;
                this.clearCache();
                this.clearCacheInfo();
                this.isUpgradingFirmware = false;
                this.infoText = null;
                this._firmwareUpgradeProgressNotification = null;
                const msg = Constants.Strings.SMRTScapeUnableToUpdateDevice;
                this.messageBoxService.displayMessageBox(msg, MessageBoxType.Error, this.resetFwUpdateCancelledFlag.bind(this), this.resetFwUpdateCancelledFlag.bind(this));
                this.clearFirmwareProgressTimer();
        }

        if (this._firmwareUpgradeProgressNotification !== null) {
            switch (this._firmwareUpgradeProgressNotification.DeviceType) {
                case Toro.DeviceTypeId.Gateway:
                    if (this._firmwareUpgradeProgressNotification.DeviceType === Toro.DeviceTypeId.Gateway && this._firmwareUpgradeProgressNotification.PercentComplete < 100) {
                        this.clearGatewayFirmwareProgressTimer();
                        // Clear any pending gateway fw upgrade completion flags.
                        this.gatewayFirmwareProgressTimerRef = setTimeout(() => {
                            this.displayExitFirmwareUpgradeMsg();
                        }, Constants.GatewayFwUgExpireTimeInSeconds);
                    } else {
                        this.clearGatewayFirmwareProgressTimer();
                    }

                    if (this._firmwareUpgradeProgressNotification.PercentComplete >= 100) {
                        if (this.infoText != null && (this.infoText.includes(Constants.Strings.Installing) || this.infoText.includes('Update in progress'))) {
                            this.infoText = Constants.Strings.FinalizingGatewayFirmwareUpdate;
                            setTimeout(() => {
                                if (this.infoText != null && this.infoText.includes(Constants.Strings.Finalizing)) {
                                    this.firmwareUpgradedCallback();
                                }
                            }, Constants.GatewayFwUgExpireTimeInSeconds);
                        }
                        else {
                            this.infoText = Constants.Strings.InstallingGatewayFw;
                            setTimeout(() => {
                                if (this.infoText != null && this.infoText.includes(Constants.Strings.Installing)) {
                                    this.firmwareUpgradedCallback();
                                }
                            }, Constants.GatewayFwUgExpireTimeInSeconds);
                        }
                    }
                    else {
                        this.infoText = null;
                    }
                    break;

                case Toro.DeviceTypeId.MAGNETO:
                    if (this._firmwareUpgradeProgressNotification.PercentComplete == 100) {
                        this.clearFirmwareProgressTimer();
                        this.infoText = Constants.Strings.InstallingFw;
                    }
                    else {
                        if (this._firmwareUpgradeProgressNotification.DeviceType === Toro.DeviceTypeId.MAGNETO && this._firmwareUpgradeProgressNotification.PercentComplete < 100) {
                            this.clearMagnetoFirmwareProgressTimer();
                            // Clear any pending Magneto fw upgrade completion flags.
                            this.magnetoFirmwareProgressTimerRef = setTimeout(() => {
                                this.displayExitFirmwareUpgradeMsg();
                            }, Constants.DefaultFwUgExpireTimeInSeconds);
                        } else {
                            this.clearMagnetoFirmwareProgressTimer();
                        }
                    }
                    break;
                // NOTE: WE ARE PURPOSELY FALLING THROUGH THIS CASE STATEMENT.
                case Toro.DeviceType.KD2:
                    if (deviceTypeName === '') deviceTypeName = 'KD2';
                case Toro.DeviceTypeId['Evo Smart Connect']:
                    if (deviceTypeName === '') deviceTypeName = 'Smart Connect';
                case Toro.DeviceTypeId.Evolution:
                    const deviceShortUId = UtilityService.getShortUIdFromFullUId(this._firmwareUpgradeProgressNotification.DeviceUId);
                    if (deviceTypeName === '') deviceTypeName = 'Controller';

                    if (
                        this._firmwareUpgradeProgressNotification.PercentComplete !== 100 ||
                        this._firmwareUpgradeProgressNotification.StatusCode === Toro.StatusCodes.DownloadInProgress
                    ) {
                        if (this._firmwareUpgradeProgressNotification.PercentComplete === 98) {
                            this.infoText = deviceTypeName + ' upgrade in progress...';
                            setTimeout(function () {
                                const futureTimeStamp = new Date();
                                futureTimeStamp.setMinutes(futureTimeStamp.getMinutes() + 3);
                                localStorage.setItem('activeFwUpdateDeviceUId:' + deviceShortUId, futureTimeStamp.toString());
                            }, 10000);
                        }
                        else {
                            this.infoText = null;
                        }
                    } else {
                        // complete
                        const now = moment();
                        if (!this.isGatewayFwUpdateActive(this._firmwareUpgradeProgressNotification.DeviceUId, now) &&
                            !this.isEvoAddReplaceActive(this._firmwareUpgradeProgressNotification.DeviceUId, now)) {
                            // If KD2, download is complete, now we must install the FW (~8 minutes)
                            if (this._firmwareUpgradeProgressNotification.DeviceType === Toro.DeviceType.KD2) {
                                if (this._isPendingConfigurationRetrieval) return;

                                this._isPendingConfigurationRetrieval = true;
                                const interval = 10;
                                let timeRemaining = 8;
                                this.infoText = `Installing Firmware (approximate time remaining: ${timeRemaining} mins)`;

                                this.firmwareCountdownInterval = setInterval(() => {
                                    timeRemaining = timeRemaining - 1 / interval;
                                    this.infoText = `Installing Firmware (approximate time remaining: ${timeRemaining < 1 ? '< 1' : Math.round(timeRemaining)}${timeRemaining > 1 ? ' mins' : ' min'})`;
                                    this.overlayManager.showProcessingOverlay.next(this.infoText);

                                    if (timeRemaining < 0) {
                                        this.overlayManager.hideProcessingOverlay.next();
                                        this.messageBoxService.displayMessageBox(Constants.Strings.FirmwareUpgradingError,
                                            MessageBoxType.Error, null, null, Constants.Strings.ControllerAddedInNonProgrammableMode);
                                        clearInterval(this.firmwareCountdownInterval);
                                    }
                                }, interval * 1000);
                            } else {
                                this.infoText = null;
                                this.firmwareUpgradedCallback();
                            }
                        } else {
                            this.infoText = Constants.Strings.UpdateInProgress;
                            setTimeout(() => {
                                if (this.infoText != null) {
                                    this.infoText = null;
                                    this.firmwareUpgradedCallback();
                                }
                            }, Constants.DefaultFwUgExpireTimeInSeconds);
                        }
                    }
                    break;

                case Toro.DeviceType.CLM1:
                case Toro.DeviceType.CLMR:
                    this.clearFirmwareProgressTimer();

                    if (this._firmwareUpgradeProgressNotification.PercentComplete >= 100) {
                        if (!this._isPendingConfigurationRetrieval) this._isPendingConfigurationRetrieval = true;
                    }
                    else if (this._firmwareUpgradeProgressNotification.PercentComplete === 0) {
                        this.firmwareProgressTimerRef = setTimeout(() => {
                            this._firmwareUpgradeProgressNotification = null;
                            this.infoText = null;
                            this.clearCacheInfo();

                            const msg = Constants.Strings.UnableToUpdateCommunicationCard;
                            this.messageBoxService.displayMessageBox(msg, MessageBoxType.Error, () => {
                                this.router.navigate([`/dashboard/${this.locationManager.currentLocation.Id}/irrigation/main`], { queryParams: { useCache: false } });
                            }, null, Constants.Strings.ControllerAddedInNonProgrammableMode);

                        }, 60 * 1000);
                    }
                    break;

                    case Toro.DeviceType.LXCSC:
                    let smrtrDeviceShortUId = UtilityService.getShortUIdFromFullUId(this._firmwareUpgradeProgressNotification.DeviceUId);

                    if (deviceTypeName === '') deviceTypeName = 'SMRT-R';

                    if (
                        this._firmwareUpgradeProgressNotification.PercentComplete !== 100 ||
                        this._firmwareUpgradeProgressNotification.StatusCode === Toro.StatusCodes.DownloadInProgress
                    ) {
                        if (this._firmwareUpgradeProgressNotification.PercentComplete === 98) {
                            this.infoText = deviceTypeName + ' upgrade in progress...';
                            setTimeout(function () {
                                const futureTimeStamp = new Date();
                                futureTimeStamp.setMinutes(futureTimeStamp.getMinutes() + 3);
                                localStorage.setItem('activeFwUpdateDeviceUId:' + smrtrDeviceShortUId, futureTimeStamp.toString());
                            },
                                10000);
                        }
                        else {
                            this.infoText = null;
                        }
                    } else {
                        // complete
                        const now = moment();
                        if (this.isLXCAddReplaceActive(this._firmwareUpgradeProgressNotification.DeviceUId, now)) {
                            this.infoText = Constants.Strings.UpdateInProgress;
                            setTimeout(() => {
                                if (this.infoText != null) {
                                    this.infoText = null;
                                    this.firmwareUpgradedCallback();
                                }
                            }, Constants.DefaultFwUgExpireTimeInSeconds);
                        }
                    }
                    break;

            }
        }
        if (this.infoText) {
            this.overlayManager.showProcessingOverlay.next(this.infoText);
        }
    }

    private displayExitFirmwareUpgradeMsg() {
        if (!this._firmwareUpgradeProgressNotification || this._firmwareUpgradeProgressNotification == null)
            return;

        const msg = Constants.Strings.FirmwareUpgradeResume; //Constants.Strings.SMRTScapeUnableToUpdateGateway;
        this._firmwareUpgradeProgressNotification = null;
        this.infoText = null;
        this.clearCacheInfo();
        this.messageBoxService.displayMessageBox(msg, MessageBoxType.Info,
            null,
            null,
            Constants.Strings.FwUgFailed);
    }

    cancelFirmwareUpgradeConfirmationCallback(deviceId: number, deviceTypeId: number, button) {
        if (button === MessageBoxButton.Yes) {
            this.overlayManager.showProcessingOverlay.next(Constants.Strings.Canceling);

            this.cancelFirmwareUpgrade(deviceId, deviceTypeId)
                .pipe(
                    finalize(() => {
                        //this.navigateAfterCompletion();
                    })
                )
                .subscribe(() => {
                    this.overlayManager.hideProcessingOverlay.next();
                    this.messageBoxService.displayMessageBox(Constants.Strings.FwUgCancelled, MessageBoxType.Success,
                        () => { this.cancelUpgradeCallback(deviceTypeId) },
                        () => { this.cancelUpgradeCallback(deviceTypeId) });
                }, err => {
                    this.overlayManager.hideProcessingOverlay.next();
                    this.messageBoxService.displayMessageBox(err['error']['Message'], MessageBoxType.Error);
                });
        }
    }

    cancelUpgradeCallback(deviceTypeId) {
        this.firmwareUpgradeCancelled.next();
        if (deviceTypeId === Toro.DeviceTypeId.Gateway) {
            //clear timer if gateway firmware upgrade exit clicked
            this.clearGatewayFirmwareProgressTimer();
        } else if (deviceTypeId === Toro.DeviceTypeId.MAGNETO) {
            //clear timer if magneto firmware upgrade exit clicked
            this.clearMagnetoFirmwareProgressTimer();
        } else if (deviceTypeId === Toro.DeviceTypeId.LXCSC) {
            //clear timer if magneto firmware upgrade exit clicked
            this.clearLXCFirmwareProgressTimer();
        }
    }

    private clearFirmwareProgressTimer() {
        if (this.firmwareProgressTimerRef) clearTimeout(this.firmwareProgressTimerRef);
    }

    public clearGatewayFirmwareProgressTimer() {
        if (this.gatewayFirmwareProgressTimerRef) clearTimeout(this.gatewayFirmwareProgressTimerRef);
    }

    public clearMagnetoFirmwareProgressTimer() {
        if (this.magnetoFirmwareProgressTimerRef) clearTimeout(this.magnetoFirmwareProgressTimerRef);
    }

    public clearLXCFirmwareProgressTimer() {
        if (this.lxcFirmwareProgressTimerRef) clearTimeout(this.lxcFirmwareProgressTimerRef);
    }

    private clearFwUpdateProgressTimestamps(deviceUId) {
        const shortUId = (deviceUId.length === 6) ? deviceUId : UtilityService.getShortUIdFromFullUId(deviceUId);
        localStorage.removeItem('gatewayFwCompletionPendingGwId:' + shortUId);
        localStorage.removeItem('activeFwUpdateDeviceUId:' + shortUId);
        localStorage.removeItem('evoAddReplaceActiveId:' + shortUId);
        localStorage.removeItem('LXCAddReplaceActiveId:' + shortUId);

        // During a Controller Add/Replace Op, we will end up with an entry for both the Controller and the Gateway.
        // If we cancel, we will only receive the message for the GW Id. The following code will remove the related
        // controller entry.
        const lsKeys = Object.keys(localStorage);
        if(lsKeys){
                lsKeys.forEach(function (key) {
                    if (key.startsWith('evoAddReplaceActiveId:')) {
                        localStorage.removeItem(key);
                    }
            });
        }
    }

    private isDeviceFwUpdateActive(uId: string, now: Moment): boolean {
        const shortUId = uId.length === 6 ? uId : UtilityService.getShortUIdFromFullUId(uId);
        const activeFwUpdateTimeStamp = new Date(localStorage.getItem('activeFwUpdateDeviceUId:' + shortUId));

        return (activeFwUpdateTimeStamp && (moment.duration(now.diff(moment(activeFwUpdateTimeStamp).toDate())).asMinutes() < 3));
    }

    private isGatewayFwUpdateActive(uId: string, now: Moment): boolean {
        const shortUId = uId.length === 6 ? uId : UtilityService.getShortUIdFromFullUId(uId);
        const gwFwCompletionPendingTimeStamp = new Date(localStorage.getItem('gatewayFwCompletionPendingGwId:' + shortUId));

        return (gwFwCompletionPendingTimeStamp && (moment.duration(now.diff(moment(gwFwCompletionPendingTimeStamp).toDate())).asMinutes() < 2));
    }

    private isEvoAddReplaceActive(uId: string, now: Moment): boolean {
        const shortUId = uId.length === 6 ? uId : UtilityService.getShortUIdFromFullUId(uId);
        const evoAddReplaceActiveTimeStamp = new Date(localStorage.getItem('evoAddReplaceActiveId:' + shortUId));

        return (evoAddReplaceActiveTimeStamp && (moment.duration(now.diff(moment(evoAddReplaceActiveTimeStamp).toDate())).asMinutes() < 6));
    }

    private isLXCAddReplaceActive(uId: string, now: Moment): boolean {
        const shortUId = uId.length === 6 ? uId : UtilityService.getShortUIdFromFullUId(uId);
        const LXCAddReplaceActiveTimeStamp = new Date(localStorage.getItem('LXCAddReplaceActiveId:' + shortUId));

        return (LXCAddReplaceActiveTimeStamp && (moment.duration(now.diff(moment(LXCAddReplaceActiveTimeStamp).toDate())).asMinutes() < 6));
    }

    private checkIfStillPreparingForUpgrade() {
        setTimeout(() => {
            if (this.infoText != null && this.infoText.includes('Preparing for Firmware Upgrade')) {
                this.clearCacheInfo();
                this.infoText = null;
                this.noFirmwareUpgradeReceived.next();
            }
        }, Constants.DefaultFwUgExpireTimeInSeconds);
    }

    removeLXCAddReplaceActive(notification){
        let deviceUId = notification.UId;
        const shortUId = (deviceUId.length === 6) ? deviceUId : UtilityService.getShortUIdFromFullUId(deviceUId);
        localStorage.removeItem('LXCAddReplaceActiveId:' + shortUId);
        this.clearCacheInfo();

        //this.clearFwUpdateProgressTimestamps();
    }

    goToDashboard(locationId: number, useCache: boolean) {
        this.router.navigate([`/dashboard/${locationId}/irrigation/main`], { queryParams: { useCache: useCache } });
    }

}

